import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Typography, Accordion, AccordionSummary, AccordionDetails, useMediaQuery } from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { assign } from 'lodash';
import * as authService from '../services/auth';
import { Roles } from '../models/roles';
import * as sitesActions from '../actions/sites';
import * as reportsActions from '../actions/reports';
import VolumeReportForm from '../components/VolumeReportForm';
import VolumeReportTable from '../components/VolumeReportTable';
import useUIFilter from '../hooks/useUIFilter';
import {
  dateRangeOptionsList,
  defaultGranularityOptions,
} from '../components/DateAndGranularityPicker/DateAndGranularityPicker';
import OverlayLoader from '../components/OverlayLoader';

export const VOLUME_REPORT_UI_FILTER = 'report-volume';
const VolumeReportView = () => {
  const dispatch = useDispatch();
  const isMediumHeight = useMediaQuery('(min-height: 680px)');
  const [filtersExpanded, setFiltersExpanded] = useState(true);
  const sites = useSelector(state => state.entities.sites);
  const volumeReport = useSelector(state => state.entities.reports.volume);
  const { setUIFilter, getUIFilter } = useUIFilter();
  const filters = assign(
    {
      period: dateRangeOptionsList.last90Days.value,
      granularity: defaultGranularityOptions.monthly.value,
      dateRange: null,
      type: { id: 'food_donors', name: 'Food Donors' },
      sites: [],
      showRescueSizeLegend: true,
      completedRegistration: null,
    },
    getUIFilter(VOLUME_REPORT_UI_FILTER)
  );

  const activeUser = authService.getCurrentlyLoggedInOrImpersonatingUser();
  const sitesWithSDSCAccess = authService.getUserSitesByRoles(activeUser.role_assignments, [
    Roles.SiteDirector,
    Roles.SiteCoordinator,
  ]);
  const isAdmin = authService.currentlyLoggedInOrImpersonatingUserHasAnyRoleInCurrentlySelectedSite([
    Roles.Admin,
    Roles.NationalSiteDirector,
  ]);
  let sitesList = isAdmin
    ? Object.values(sites.byId)
    : Object.values(sites.byId).filter(site => sitesWithSDSCAccess.includes(site.id));

  // sort sites
  sitesList.sort((a, b) => a.name.localeCompare(b.name));

  useEffect(() => {
    dispatch(sitesActions.fetchSitesIfNeeded());
  }, []);

  const handleFiltersChange = values =>
    setUIFilter(VOLUME_REPORT_UI_FILTER, {
      sites: values.sites,
      period: values.period,
      type: values.type,
      granularity: values.granularity,
      suppressEmptyRows: values.suppress_empty_rows,
      showRescueSizeLegend: values.showRescueSizeLegend,
      dateRange: {
        startDate: values.startDate,
        endDate: values.endDate,
      },
      completedRegistration: values.completedRegistration,
    });

  const handleChange = (event, isExpanded) => setFiltersExpanded(isExpanded);

  const fetchReport = () => {
    if (
      filters.dateRange
      && filters.dateRange.startDate !== null
      && filters.dateRange.endDate !== null
      && sitesList.length
    ) {
      // if user selected sites, use it and submit
      // otherwise - check if user is an admin -> then send an empty array to receive ALL sites
      // if user is not an admin, send an array with the sites ids in which user is SD/SC
      const sitesIds = (() => {
        if (filters.sites && filters.sites.length) {
          return filters.sites.map(s => s.id);
        }

        if (isAdmin) {
          return [];
        }

        return sitesList.map(s => s.id);
      })();

      dispatch(
        reportsActions.fetchVolumeReportIfNeeded(
          filters.type.id,
          filters.dateRange.startDate,
          filters.dateRange.endDate,
          filters.granularity,
          sitesIds,
          filters.suppressEmptyRows,
          filters.completedRegistration
        )
      );
    }
  };

  const setReportTableHeight = () => {
    const filtersHeight = filtersExpanded ? 275 : 48;
    const tableContainer = document.querySelector('div.table-container');

    if (tableContainer) {
      tableContainer.style.height =
        isMediumHeight || !filtersExpanded ? `calc(100vh - ${225 + filtersHeight}px)` : '50vh';
    }
  };

  useEffect(() => {
    setReportTableHeight();
  }, [filtersExpanded]);

  if (!sitesList.length) {
    return 'Loading sites...';
  }

  return (
    <OverlayLoader isLoading={sites.inflight || volumeReport.inflight}>
      <Grid container>
        <Grid item xs={12}>
          <Accordion expanded={filtersExpanded} onChange={handleChange}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6" color="textPrimary">
                Volume Report
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <VolumeReportForm
                defaultValues={filters}
                isAdmin={isAdmin}
                sitesList={sitesList}
                onSubmit={handleFiltersChange}
                fetchReport={fetchReport}
              />
            </AccordionDetails>
          </Accordion>

          <VolumeReportTable
            type={filters.type}
            volume={volumeReport.all}
            showSiteColumn={filters.sites.length > 1 || (!filters.sites.length && sitesList.length > 1)}
            filters={filters}
          />
        </Grid>
      </Grid>
    </OverlayLoader>
  );
};

export default VolumeReportView;
